// src/api/adminSchedules.ts
import axios from './axios';

export type IsoDate = string; // 'YYYY-MM-DD'
export type HHMM = string;    // 'HH:MM'

// —— 页面里用到的目标类型——
export interface SlotInfo {
  time: HHMM;
  status: 'available' | 'limited' | 'full' | 'none';
  scheduled_doctor_ids: number[]; // 该时段已排班的医生ID（用于显示容量）
  used: number;                   // 已被预约数量
  max: number;                    // 最大可并发
  remaining: number;              // 剩余可预约
}

export interface DayRow {
  date: IsoDate;
  slots: SlotInfo[];
}

export interface Doctor {
  id: number;
  name: string;                 // 页面里当成 string 使用
  email?: string | undefined;   // 不能是 null，否则 setState 报错
  active?: boolean;
}

// =======================
// 通用时间工具（新增）
// =======================

// 标准化为 'HH:MM'（补零）
export const toHHMM = (s: string): HHMM => {
  const parts = String(s).split(':');
  const h = parts[0] ?? '0';
  const m = parts[1] ?? '00';
  return `${String(Number(h)).padStart(2, '0')}:${String(Number(m)).padStart(2, '0')}`;
};

// 非补零 'H:MM'
const toHMM = (s: string): string => {
  const parts = String(s).split(':');
  const h = parts[0] ?? '0';
  const m = parts[1] ?? '00';
  return `${Number(h)}:${String(Number(m)).padStart(2, '0')}`;
};

// 'HH:MM:00'
const toHHMMss = (s: string): string => `${toHHMM(s)}:00`;

// 'H:MM:00'
const toHMMss = (s: string): string => `${toHMM(s)}:00`;

// 生成多种可能时间写法，便于后端“对得上”
const normalizeTimeCandidates = (time: string): string[] => {
  // 去掉秒
  const t = String(time).split(':').slice(0, 2).join(':');
  return [toHHMM(t), toHMM(t), toHHMMss(t), toHMMss(t)];
};

// =======================
// 医师列表（用于筛选）
// =======================
export async function fetchDoctors(): Promise<Doctor[]> {
  const res = await axios.get('/admin/schedules/doctors');
  const list = (res.data ?? []).map((d: any) => {
    const displayName =
      (d.display_name && String(d.display_name).trim()) ||
      (d.full_name && String(d.full_name).trim()) ||
      (d.name && String(d.name).trim()) ||
      (d.email && String(d.email).trim()) ||
      `Doctor#${d.id}`;
    return {
      id: d.id,
      name: displayName,
      email: d.email ?? undefined,   // 统一成 string | undefined
      active: d.active ?? true,
    } as Doctor;
  });
  return list;
}

// =======================
// 日历矩阵（管理视角）
// 期望返回 DayRow[]（含 used/max/remaining 等字段）
// 后端若只返回 status，则在前端兜底补齐。
// =======================
type BackendCalendarSlot =
  | {
      time: HHMM;
      status: 'available' | 'limited' | 'full' | 'none';
      scheduled_doctor_ids?: number[];
      used?: number;
      max?: number;
      remaining?: number;
    }
  | { time: HHMM; status: string }; // 简版兜底

type BackendCalendarDay = {
  date: IsoDate;
  slots: BackendCalendarSlot[];
};

export async function fetchAdminScheduleMatrix(start: IsoDate, days: number): Promise<DayRow[]> {
  // 实际后端路径：/admin/schedules/calendar?start=YYYY-MM-DD&days=7
  const res = await axios.get('/admin/schedules/calendar', { params: { start, days } });
  const data: BackendCalendarDay[] = res.data ?? [];

  // 兜底策略：
  // - scheduled_doctor_ids: []
  // - used: 0
  // - max: 3
  // - remaining: max - used（>=0）
  const DEFAULT_MAX = 3;

  const mapped: DayRow[] = data.map((d) => {
    const slots: SlotInfo[] = (d.slots ?? []).map((s: any) => {
      const statusRaw = s?.status;
      const status: SlotInfo['status'] =
        statusRaw === 'available' || statusRaw === 'limited' || statusRaw === 'full' || statusRaw === 'none'
          ? statusRaw
          : 'none';

      const scheduled_doctor_ids = s?.scheduled_doctor_ids ?? [];
      const used = s?.used ?? 0;
      const max = s?.max ?? DEFAULT_MAX;
      const remainingRaw = s?.remaining ?? (max - used);
      const remaining = Math.max(0, remainingRaw);

      return {
        time: toHHMM(s?.time ?? '00:00'), // 统一为 HH:MM
        status,
        scheduled_doctor_ids,
        used,
        max,
        remaining,
      };
    });
    return { date: d.date, slots };
  });

  return mapped;
}

// 兼容别名（有些页面可能还在用旧名称）
export const fetchAdminCalendar = fetchAdminScheduleMatrix;

// =======================
// 并发上限：拉区间（start, days）
// 需要 Record<date, max_capacity>
// 若后端没有批量接口，前端循环 GET 某日上限兜底
// =======================
export async function fetchDayCapacity(date: IsoDate): Promise<{ date: IsoDate; max_capacity: number }> {
  const res = await axios.get('/admin/schedules/day-capacity', { params: { date } });
  return res.data;
}

// 页面里调用的是 fetchDayCaps(start, days)
export async function fetchDayCaps(start: IsoDate, days: number): Promise<Record<string, number>> {
  // 如果你后端有批量接口（比如 /admin/schedules/day-capacities?start=&days=）
  // 可以改成一次请求拿完；这里做逐日请求的兜底：
  const startDate = new Date(start + 'T00:00:00');
  const tasks: Promise<{ date: IsoDate; max_capacity: number }>[] = [];

  for (let i = 0; i < days; i++) {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const dateStr: IsoDate = `${y}-${m}-${dd}`;
    tasks.push(fetchDayCapacity(dateStr));
  }

  const results = await Promise.allSettled(tasks);
  const out: Record<string, number> = {};
  for (const r of results) {
    if (r.status === 'fulfilled' && r.value?.date) {
      out[r.value.date] = r.value.max_capacity ?? 3;
    }
  }
  return out;
}

// =======================
// 切换排班（单个时段开/关）
// enabled=true -> 添加；false -> 移除
// =======================
export async function addDoctorSlots(payload: {
  doctor_id: number;
  date: IsoDate;
  times: HHMM[];
}): Promise<{ inserted: number }> {
  const res = await axios.post('/admin/schedules/slots', payload);
  return res.data;
}

export async function removeDoctorSlots(payload: {
  doctor_id: number;
  date: IsoDate;
  times: HHMM[];
}): Promise<{ deleted: number }> {
  // axios.delete 不带 body 时不好传参，所以用 axios.request
  const res = await axios.request({
    url: '/admin/schedules/slots',
    method: 'DELETE',
    data: payload,
  });
  return res.data;
}

/**
 * 智能删除（新增）：
 * - 同一时间尝试多种格式（'HH:MM' / 'H:MM' / 'HH:MM:00' / 'H:MM:00'）
 * - 直到后端返回成功；兼容不同返回结构（deleted 字段、数组、或仅 200）
 */
export async function removeOneSlotSmart(
  doctor_id: number,
  date: IsoDate,
  time: string
): Promise<{ deleted: number } | null> {
  const candidates = normalizeTimeCandidates(time);
  for (const t of candidates) {
    try {
      const resp = await removeDoctorSlots({ doctor_id, date, times: [t as HHMM] });
      const deleted =
        (resp as any)?.deleted ??
        (resp as any)?.data?.deleted ??
        (Array.isArray((resp as any)?.data) ? (resp as any).data.length : undefined) ??
        1; // 没有明确返回时，HTTP 200 视为 1
      if (Number(deleted) > 0) {
        return { deleted: Number(deleted) };
      }
    } catch {
      // 本格式失败，继续尝试下一种
    }
  }
  return null;
}

export async function toggleDoctorSchedule(args: {
  doctor_id: number;
  date: IsoDate;
  time: HHMM;
  enabled: boolean;
}): Promise<void> {
  const { doctor_id, date, time, enabled } = args;
  const hhmm = toHHMM(time);
  if (enabled) {
    await addDoctorSlots({ doctor_id, date, times: [hhmm] });
  } else {
    // 这里也走智能删除，更稳
    await removeOneSlotSmart(doctor_id, date, hhmm);
  }
}

// =======================
// 设置某天的并发上限（页面里用 setDayCap(date, max)）
// =======================
export async function setDayCapacity(payload: {
  date: IsoDate;
  max_capacity: number;
}): Promise<{ date: IsoDate; max_capacity: number }> {
  const res = await axios.put('/admin/schedules/day-capacity', payload);
  return res.data;
}

export async function setDayCap(date: IsoDate, max: number): Promise<{ date: IsoDate; max_capacity: number }> {
  return setDayCapacity({ date, max_capacity: max });
}
